import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "../ui/input";

export function DataTable({
    columns,
    rows,
    search,
    rowKey = (r) => r.id,
    pageSize = 50,
    onRowClick,
    filtersSlot,
    actionsSlot,
    testId = "data-table",
}) {
    // #136 Befund 6: Suche, Seite und Sortierung leben in der URL, damit der
    // Kontext ein Detail-und-zurück (und Reload/Teilen) überlebt. Fremde
    // Query-Params (dq, filter, overdue, status, ...) bleiben unangetastet.
    const [searchParams, setSearchParams] = useSearchParams();
    const searchText = searchParams.get("q") || "";
    const page = Math.max(0, parseInt(searchParams.get("p") || "0", 10) || 0);
    const sortKey = searchParams.get("sort") || null;
    const sortDir = searchParams.get("dir") === "desc" ? "desc" : "asc";
    const setParams = (updates) =>
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            for (const [key, value] of Object.entries(updates)) {
                if (value === null || value === "" || value === undefined) next.delete(key);
                else next.set(key, String(value));
            }
            return next;
        }, { replace: true });
    const setSearchText = (value) => setParams({ q: value, p: null });
    const setPage = (value) => {
        const v = typeof value === "function" ? value(page) : value;
        setParams({ p: v > 0 ? v : null });
    };

    const filtered = useMemo(() => {
        let arr = rows;
        if (search && searchText) {
            const s = searchText.toLowerCase();
            arr = arr.filter((r) => search(r).toLowerCase().includes(s));
        }
        if (sortKey) {
            arr = [...arr].sort((a, b) => {
                const av = a[sortKey] ?? "";
                const bv = b[sortKey] ?? "";
                if (av < bv) return sortDir === "asc" ? -1 : 1;
                if (av > bv) return sortDir === "asc" ? 1 : -1;
                return 0;
            });
        }
        return arr;
    }, [rows, search, searchText, sortKey, sortDir]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
    const pageRows = filtered.slice(page * pageSize, (page + 1) * pageSize);

    const toggleSort = (k) => {
        if (sortKey === k) setParams({ dir: sortDir === "asc" ? "desc" : null });
        else setParams({ sort: k, dir: null });
    };

    return (
        <div className="flex flex-col gap-3" data-testid={testId}>
            <div className="flex flex-wrap items-center gap-3">
                {search ? (
                    <Input
                        placeholder="Suchen..."
                        aria-label="Tabelle durchsuchen"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="h-10 w-72"
                        data-testid={`${testId}-search`}
                    />
                ) : null}
                {filtersSlot}
                <div className="ml-auto">{actionsSlot}</div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border bg-card/40">
                <table className="w-full text-sm">
                    <thead className="bg-card/60 text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            {columns.map((c) => (
                                <th
                                    key={c.key}
                                    aria-sort={
                                        sortKey === c.key
                                            ? sortDir === "asc" ? "ascending" : "descending"
                                            : undefined
                                    }
                                    className="px-3 py-2 text-left font-medium"
                                >
                                    {/* #136 Befund 5: Sortierung als echter Button,
                                        per Tastatur erreichbar. */}
                                    {c.sortable !== false && c.label ? (
                                        <button
                                            type="button"
                                            onClick={() => toggleSort(c.key)}
                                            className="inline-flex items-center gap-1 uppercase"
                                        >
                                            {c.label}
                                            {sortKey === c.key ? (
                                                sortDir === "asc" ? (
                                                    <ChevronUp className="h-3 w-3" />
                                                ) : (
                                                    <ChevronDown className="h-3 w-3" />
                                                )
                                            ) : null}
                                        </button>
                                    ) : (
                                        <span className="inline-flex items-center gap-1">{c.label}</span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody data-testid={`${testId}-body`}>
                        {pageRows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="px-3 py-8 text-center text-xs text-muted-foreground">
                                    Keine Einträge.
                                </td>
                            </tr>
                        ) : (
                            pageRows.map((r) => (
                                <tr
                                    key={rowKey(r)}
                                    onClick={() => onRowClick?.(r)}
                                    // #136 Befund 5: klickbare Zeilen auch per
                                    // Tastatur (Tab + Enter/Leertaste).
                                    tabIndex={onRowClick ? 0 : undefined}
                                    onKeyDown={
                                        onRowClick
                                            ? (e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    onRowClick(r);
                                                }
                                            }
                                            : undefined
                                    }
                                    className={`border-t border-border/60 ${onRowClick ? "cursor-pointer hover:bg-card/80" : ""}`}
                                    data-testid={`${testId}-row-${rowKey(r)}`}
                                >
                                    {columns.map((c) => (
                                        <td key={c.key} className="px-3 py-2 text-foreground">
                                            {c.render ? c.render(r) : r[c.key] ?? "—"}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {pageCount > 1 ? (
                <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
                    <button
                        type="button"
                        disabled={page === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        className="rounded border border-border px-2 py-1 disabled:opacity-30"
                        data-testid={`${testId}-prev`}
                    >
                        ← zurück
                    </button>
                    <span>
                        Seite {page + 1} / {pageCount} — {filtered.length} Zeilen
                    </span>
                    <button
                        type="button"
                        disabled={page + 1 >= pageCount}
                        onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                        className="rounded border border-border px-2 py-1 disabled:opacity-30"
                        data-testid={`${testId}-next`}
                    >
                        weiter →
                    </button>
                </div>
            ) : null}
        </div>
    );
}

export function exportCsv(filename, rows, columns, separator = ";") {
    const header = columns.map((c) => c.label).join(separator);
    const escape = (v) => {
        const s = v == null ? "" : String(v);
        return s.includes(separator) || s.includes("\n") || s.includes('"')
            ? `"${s.replace(/"/g, '""')}"`
            : s;
    };
    const body = rows
        .map((r) =>
            columns
                .map((c) => escape(c.export ? c.export(r) : r[c.key]))
                .join(separator)
        )
        .join("\n");
    const blob = new Blob(["\uFEFF" + header + "\n" + body], {
        type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export function AdminPageHeader({ title, subtitle, children, wrapActions = false }) {
    return (
        <div className={`mb-6 flex items-start justify-between ${wrapActions ? "flex-wrap gap-4" : ""}`}>
            <div className={wrapActions ? "min-w-0 max-w-full break-words" : undefined}>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Admin Console
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground lg:text-4xl">
                    {title}
                </h1>
                {subtitle ? (
                    <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
                ) : null}
            </div>
            <div className={`flex items-center gap-2 ${wrapActions ? "max-w-full flex-wrap" : ""}`}>{children}</div>
        </div>
    );
}
